import React from "react";
import {
  Box,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  useTheme,
  alpha,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink } from "react-router-dom";

/**
 * PageContainer
 *
 * Standardizes the layout for every page in the system.
 *
 * Structure:
 * 1. Outer Box (Fixed Height, Hidden Overflow) -> Replaces 'pageLayout'
 * 2. Header Section (Title, Breadcrumbs, Actions) -> Replaces 'headerLayout'
 * 3. Content Box (Scrollable) -> Replaces 'innerLayout'
 */
const PageContainer = ({
  title,
  subtitle,
  breadcrumbs = [], // Array of { label, to }
  actions, // Buttons/Controls for top right
  children,
  loading = false,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%", // Takes full height of parent (MainLayout)
        width: "100%",
        overflow: "hidden", // Prevents window scrollbar, forces internal scroll
        bgcolor:
          theme.palette.mode === "dark" ? "background.default" : "#f4f6f8", // Subtle contrast bg
        position: "relative",
      }}
    >
      {/* --- HEADER SECTION --- */}
      <Box
        sx={{
          // --- HEADER LAYOUT LOGIC ---
          display: "flex",
          flexDirection: "column",
          width: "100%",
          px: { xs: 2, md: 3 }, // Responsive padding
          pt: { xs: 2, md: 3 },
          pb: 2,
          flexShrink: 0, // Header never shrinks
        }}
      >
        {/* Breadcrumbs (Optional) */}
        {breadcrumbs.length > 0 && (
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ mb: 1, "& .MuiTypography-root": { fontSize: "0.85rem" } }}
          >
            {breadcrumbs.map((crumb, index) => (
              <div key={index}>
                {crumb.to ? (
                  <Link
                    component={RouterLink}
                    to={crumb.to}
                    color="inherit"
                    underline="hover"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <Typography color="text.primary">{crumb.label}</Typography>
                )}
              </div>
            ))}
          </Breadcrumbs>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          {/* Title & Subtitle */}
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Page Actions (Add Button, Export, etc.) */}
          {actions && <Box>{actions}</Box>}
        </Stack>
      </Box>

      {/* --- CONTENT SECTION --- */}
      <Box
        sx={{
          // --- INNER LAYOUT LOGIC ---
          flex: 1, // Takes all remaining height
          overflowY: "auto", // SCROLL HAPPENS HERE
          width: "100%",
          px: { xs: 2, md: 3 },
          pb: 3,
          // Custom scrollbar styling for webkit
          "&::-webkit-scrollbar": {
            width: "5px", // Sleek width
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent", // Clean track
            margin: "4px 0", // Adds slight padding to top/bottom of track
          },
          "&::-webkit-scrollbar-thumb": {
            // Uses text color for guaranteed contrast in light/dark mode
            backgroundColor: alpha(theme.palette.text.primary, 0.25),
            borderRadius: "10px", // Fully rounded pill shape
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: alpha(theme.palette.text.primary, 0.45), // Darkens noticeably on hover
          },
        }}
      >
        {/* Render the actual page content */}
        {children}
      </Box>
    </Box>
  );
};

export default PageContainer;
